import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { FaLinkedin, FaInstagram } from "react-icons/fa";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark, faChevronLeft, faChevronRight } from "@fortawesome/free-solid-svg-icons";

import "../App.css";

const GALLERY_DATA_URL = "/gallery-data/gallery.json";
const GALLERY_IMAGE_BASE = "/gallery-data/images/";

function Gallery() {
  const [tabs, setTabs] = useState(null); // null = loading, [] = loaded-empty
  const [loadError, setLoadError] = useState(false);
  const [activeTab, setActiveTab] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetch(GALLERY_DATA_URL, { cache: "no-store" })
      .then((res) => {
        if (!res.ok) throw new Error("Gallery data not available");
        return res.json();
      })
      .then((data) => {
        if (cancelled) return;
        const loadedTabs = data.tabs || [];
        setTabs(loadedTabs);
        if (loadedTabs.length > 0) setActiveTab(loadedTabs[0].id);
      })
      .catch(() => {
        if (!cancelled) {
          setTabs([]);
          setLoadError(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const current = useMemo(
    () => (tabs || []).find((t) => t.id === activeTab) || null,
    [tabs, activeTab]
  );

  // Lightbox prev/next must traverse ungrouped images followed by each
  // header's images, in order, since they're rendered as separate grids.
  const flattenedImages = useMemo(() => {
    if (!current) return [];
    return [...(current.images || []), ...(current.headers || []).flatMap((h) => h.images || [])];
  }, [current]);

  const closeLightbox = useCallback(() => setLightboxIndex(null), []);
  const showPrev = useCallback(
    (e) => {
      e.stopPropagation();
      setLightboxIndex((i) => (i === 0 ? flattenedImages.length - 1 : i - 1));
    },
    [flattenedImages.length]
  );
  const showNext = useCallback(
    (e) => {
      e.stopPropagation();
      setLightboxIndex((i) => (i === flattenedImages.length - 1 ? 0 : i + 1));
    },
    [flattenedImages.length]
  );

  useEffect(() => {
    if (lightboxIndex === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowLeft") showPrev(e);
      if (e.key === "ArrowRight") showNext(e);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [lightboxIndex, closeLightbox, showPrev, showNext]);

  const renderGrid = (images, keyPrefix, startIndex) => (
    <div className="gallery-grid">
      {images.map((img, index) => (
        <button
          type="button"
          className="gallery-grid__item"
          key={`${keyPrefix}-${index}`}
          data-aos="fade-up"
          data-aos-duration={900 + index * 70}
          onClick={() => setLightboxIndex(startIndex + index)}
          aria-label={img.type === "video" ? `Play video: ${img.alt}` : `Open photo: ${img.alt}`}
        >
          {img.type === "video" ? (
            <>
              <video
                src={`${GALLERY_IMAGE_BASE}${img.src}`}
                poster={img.poster ? `${GALLERY_IMAGE_BASE}${img.poster}` : undefined}
                muted
                loop
                playsInline
                preload="metadata"
                onMouseEnter={(e) => e.currentTarget.play().catch(() => {})}
                onMouseLeave={(e) => {
                  e.currentTarget.pause();
                  e.currentTarget.currentTime = 0;
                }}
              />
              <span className="gallery-grid__play-badge" aria-hidden="true">
                <svg viewBox="0 0 24 24" width="22" height="22">
                  <circle cx="12" cy="12" r="12" fill="rgba(0,0,0,0.55)" />
                  <path d="M9 7.5v9l8-4.5-8-4.5z" fill="#fff" />
                </svg>
              </span>
            </>
          ) : (
            <img src={`${GALLERY_IMAGE_BASE}${img.src}`} alt={img.alt} loading="lazy" />
          )}
        </button>
      ))}
    </div>
  );

  return (
    <div className="page-wrapper">
      <div id="smooth-wrapper">
        <div id="smooth-content">
          <section className="gallery-intro">
            <div className="container">
              <div className="sec-title text-center">
                <h6 className="sec-title__tagline bw-split-text">Life At Epaging</h6>
                <h3 className="sec-title__title bw-split-text">Gallery</h3>
                <p className="gallery-intro__text">
                  Real moments from our team, our celebrations, and our office
                  in Karachi.
                </p>
              </div>

              {tabs === null && <p className="gallery-loading">Loading gallery…</p>}

              {tabs !== null && tabs.length === 0 && (
                <p className="gallery-loading">
                  {loadError ? "Gallery is being updated — check back soon." : "No photos yet."}
                </p>
              )}

              {tabs !== null && tabs.length > 0 && (
                <>
                  <div className="gallery-tabs" role="tablist" aria-label="Gallery categories">
                    {tabs.map((tab) => (
                      <button
                        key={tab.id}
                        role="tab"
                        aria-selected={activeTab === tab.id}
                        className={`gallery-tabs__item ${activeTab === tab.id ? "active" : ""}`}
                        onClick={() => {
                          setActiveTab(tab.id);
                          setLightboxIndex(null);
                        }}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>

                  {current && (
                    <>
                      {renderGrid(current.images || [], `${current.id}-ungrouped`, 0)}
                      {(current.headers || []).map((header, hIdx) => {
                        const precedingCount =
                          (current.images || []).length +
                          (current.headers || [])
                            .slice(0, hIdx)
                            .reduce((sum, h) => sum + (h.images || []).length, 0);
                        return (
                          <div key={header.id} className="gallery-grid__header-group">
                            <h4 className="gallery-grid__header-label">{header.label}</h4>
                            {renderGrid(header.images || [], header.id, precedingCount)}
                          </div>
                        );
                      })}
                    </>
                  )}
                </>
              )}

              <div className="gallery-follow">
                <p>Follow us for more from the team and our projects</p>
                <div className="gallery-follow__links">
                  <a
                    href="https://www.linkedin.com/company/epaging/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="gallery-follow__btn"
                  >
                    <FaLinkedin /> LinkedIn
                  </a>
                  <a
                    href="https://www.instagram.com/epagingpvtltd/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="gallery-follow__btn"
                  >
                    <FaInstagram /> Instagram
                  </a>
                </div>
              </div>

              <div className="gallery-cta">
                <Link to="/contact" className="lotech-btn">
                  <span>Get In Touch</span>
                </Link>
              </div>
            </div>
          </section>

          {lightboxIndex !== null && flattenedImages[lightboxIndex] && (
            <div className="gallery-lightbox" onClick={closeLightbox}>
              <button
                className="gallery-lightbox__close"
                onClick={closeLightbox}
                aria-label="Close"
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
              <button
                className="gallery-lightbox__nav gallery-lightbox__nav--prev"
                onClick={showPrev}
                aria-label="Previous photo"
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </button>
              {flattenedImages[lightboxIndex].type === "video" ? (
                <video
                  key={flattenedImages[lightboxIndex].src}
                  src={`${GALLERY_IMAGE_BASE}${flattenedImages[lightboxIndex].src}`}
                  poster={
                    flattenedImages[lightboxIndex].poster
                      ? `${GALLERY_IMAGE_BASE}${flattenedImages[lightboxIndex].poster}`
                      : undefined
                  }
                  controls
                  autoPlay
                  playsInline
                  onClick={(e) => e.stopPropagation()}
                />
              ) : (
                <img
                  src={`${GALLERY_IMAGE_BASE}${flattenedImages[lightboxIndex].src}`}
                  alt={flattenedImages[lightboxIndex].alt}
                  onClick={(e) => e.stopPropagation()}
                />
              )}
              <button
                className="gallery-lightbox__nav gallery-lightbox__nav--next"
                onClick={showNext}
                aria-label="Next photo"
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Gallery;
